import type { FundingApplicationSchemas } from "@wayflyer/api-types";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";

import type { ExtractionAccount } from "../../../pages/banking/hooks/use-bank-statement-extraction-statuses";

import { overlapsAirPeriods } from "./air-statement-coverage";
import type { StatementCoverageRange } from "./air-statement-coverage";

export type StatementRelevance =
  | { kind: "accepted"; ranges: StatementCoverageRange[] }
  | { kind: "wrong_account" }
  | { kind: "outside_period"; ranges: StatementCoverageRange[] };

type AirAccount = Pick<FundingApplicationSchemas.BankStatementsForAccountAIRSchema, "bank_account_mask">;

type ExtractedStatement = {
  accounts: Pick<ExtractionAccount, "bankAccountMask" | "statementStartDate" | "statementEndDate">[];
};

const isUsableMask = (mask: string | null | undefined): mask is string => mask != null && /^\d{4}$/.test(mask);

// The mask is the only signal that can rule an account out; a missing or unusable mask on either side is no signal.
const accountMatchesAir = (account: ExtractedStatement["accounts"][number], airAccount: AirAccount): boolean =>
  !isUsableMask(airAccount.bank_account_mask) ||
  !isUsableMask(account.bankAccountMask) ||
  account.bankAccountMask === airAccount.bank_account_mask;

export const getStatementRelevance = (
  statement: ExtractedStatement,
  air: AirAccount & { periods: FundingApplicationSchemas.DateTimeRangeSchema[] },
  now: Dayjs = dayjs()
): StatementRelevance => {
  const matchingAccounts = statement.accounts.filter((account) => accountMatchesAir(account, air));
  if (statement.accounts.length > 0 && matchingAccounts.length === 0) {
    return { kind: "wrong_account" };
  }

  const ranges = matchingAccounts.flatMap((account) =>
    account.statementStartDate && account.statementEndDate
      ? [{ start: account.statementStartDate, end: account.statementEndDate }]
      : []
  );
  if (ranges.length > 0 && !overlapsAirPeriods(air.periods, ranges, now)) {
    return { kind: "outside_period", ranges };
  }

  return { kind: "accepted", ranges };
};
